/**
 * Wikidaily Game Store
 *
 * Store for the daily Wikipedia challenge game.
 *
 * Store methods are called automatically by the router based on action name.
 * Example: {action: "update_game", payload: {...}} calls store.update_game(payload)
 *
 * All server-updating methods should be at the top level so the router can find them.
 */

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useAppStore } from "../../store";
import type { WikidailyState, DailyScoreboardResponse } from "./types";
import type { LobbyState } from "../../store";
import {
  queueArticleRender,
  type ArticleRenderData,
} from "../../articleRenderQueue";

type WikidailyLocalState = {
  error: string | null;
  scoreboard: DailyScoreboardResponse | null;
  isLoadingScoreboard: boolean;
};

type WikidailyStore = {
  gameState: WikidailyState | null;
  lobbyState: LobbyState | null;
  localState: WikidailyLocalState;

  // Computed
  isHost: () => boolean;

  update_game: (state: WikidailyState) => void;
  update_lobby: (lobbyState: LobbyState) => void;
  render_article: (payload: {
    article_id: number;
    html: string;
    title: string;
  }) => void;
  startGame: () => { action: string; payload?: unknown };
  giveUp: () => { action: string; payload?: unknown };

  fetchScoreboard: (language: string) => Promise<void>;
};

export const useWikidailyStore = create<WikidailyStore>()(
  immer((set, get) => ({
    gameState: null,
    lobbyState: null,
    localState: {
      error: null,
      articleHtml: null,
      renderedArticleId: null,
      scoreboard: null,
      isLoadingScoreboard: false,
    },

    isHost: () => {
      const currentPlayerId = useAppStore.getState().id;
      const hostId = get().lobbyState?.host_id;
      return currentPlayerId === hostId;
    },

    update_game: (state) => {
      const prevPhase = get().gameState?.phase;
      const prevArticleId = get().gameState?.current_article?.id;

      console.log("[update_game]", {
        phase: state.phase,
        articleId: state.current_article?.id,
        prevPhase,
        prevArticleId,
      });

      set((draft) => {
        draft.gameState = state;
        draft.localState.error = null;
      });
    },

    update_lobby: (lobbyState) => {
      console.log("[update_lobby]", lobbyState);

      set((draft) => {
        draft.lobbyState = lobbyState;
        draft.localState.error = null;
      });
    },

    render_article: (payload) => {
      const currentArticleId = get().gameState?.current_article.id;

      console.log("[render_article]", {
        htmlLength: payload.html?.length,
        currentArticleId,
      });

      if (currentArticleId === undefined || currentArticleId === null) {
        console.warn("[render_article] No current article ID, ignoring render");
        return;
      }

      const data: ArticleRenderData = {
        html: payload.html,
        articleId: payload.article_id,
        articleTitle: payload.title,
      };
      queueArticleRender(data);
    },

    startGame: () => {
      console.log("[startGame]");
      return {
        action: "start",
      };
    },

    giveUp: () => {
      console.log("[giveUp]");
      return {
        action: "complete",
      };
    },

    fetchScoreboard: async (language: string) => {
      set((draft) => {
        draft.localState.isLoadingScoreboard = true;
      });

      try {
        const response = await fetch(
          `/api/daily/scoreboard?language=${language}`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch scoreboard");
        }
        const data = await response.json();
        set((draft) => {
          draft.localState.scoreboard = data;
          draft.localState.isLoadingScoreboard = false;
        });
      } catch (error) {
        console.error("Error fetching scoreboard:", error);
        set((draft) => {
          draft.localState.isLoadingScoreboard = false;
        });
      }
    },
  })),
);

// Tests
if (import.meta.vitest) {
  const { describe, it, expect, beforeEach } = import.meta.vitest;

  describe("WikidailyStore", () => {
    beforeEach(() => {
      useWikidailyStore.setState({
        gameState: null,
        lobbyState: null,
        localState: {
          error: null,
          scoreboard: null,
          isLoadingScoreboard: false,
        },
      });
    });

    const createMockGameState = (): WikidailyState => ({
      phase: "Start",
      current_article: {
        id: 1,
        title: "Test Article",
        description: null,
      },
      challenge: {
        id: "test-id",
        language: "en",
        date: "2024-01-01",
        challenge_date: "2024-01-01",
        start_article: {
          id: 1,
          title: "Test Article",
          description: null,
        },
        target_articles: [],
      },
      found_articles: [],
      start_time: null,
      end_time: null,
      completed_successfully: null,
    });

    it("update_game updates game state", () => {
      const mockState = createMockGameState();
      useWikidailyStore.getState().update_game(mockState);
      expect(useWikidailyStore.getState().gameState).toEqual(mockState);
    });

    it("update_lobby updates lobby state", () => {
      const mockLobbyState = {
        players: {
          "player-1": { points: 0, is_ready: true, name: "Player 1" },
        },
        host_id: "host-1",
      };
      useWikidailyStore.getState().update_lobby(mockLobbyState);
      expect(useWikidailyStore.getState().lobbyState).toEqual(mockLobbyState);
    });

    it("isHost returns true when current player is host", () => {
      useAppStore.setState({ id: "host-1" });
      const mockLobbyState = {
        players: {},
        host_id: "host-1",
      };
      useWikidailyStore.getState().update_lobby(mockLobbyState);
      expect(useWikidailyStore.getState().isHost()).toBe(true);
    });

    it("isHost returns false when current player is not host", () => {
      useAppStore.setState({ id: "player-1" });
      const mockLobbyState = {
        players: {},
        host_id: "host-1",
      };
      useWikidailyStore.getState().update_lobby(mockLobbyState);
      expect(useWikidailyStore.getState().isHost()).toBe(false);
    });

    it("render_article calls queueArticleRender", () => {
      const mockState = createMockGameState();
      mockState.current_article.id = 123;
      useWikidailyStore.getState().update_game(mockState);

      useWikidailyStore.getState().render_article({
        html: "<p>test</p>",
        article_id: 123,
        title: "Test",
      });
    });

    it("startGame returns correct action", () => {
      const result = useWikidailyStore.getState().startGame();
      expect(result.action).toBe("start");
    });

    it("giveUp returns correct action", () => {
      const result = useWikidailyStore.getState().giveUp();
      expect(result.action).toBe("complete");
    });

    it("handles phase transitions", () => {
      const mockState = createMockGameState();
      mockState.phase = "Game";
      useWikidailyStore.getState().update_game(mockState);
      expect(useWikidailyStore.getState().gameState?.phase).toBe("Game");

      const scoreboardState = { ...mockState, phase: "Scoreboard" as const };
      useWikidailyStore.getState().update_game(scoreboardState);
      expect(useWikidailyStore.getState().gameState?.phase).toBe("Scoreboard");
    });

    it("handles state transitions through phases", () => {
      const phases: Array<WikidailyState["phase"]> = [
        "Start",
        "Game",
        "Scoreboard",
      ];

      phases.forEach((phase) => {
        const state = { ...createMockGameState(), phase };
        useWikidailyStore.getState().update_game(state);
        expect(useWikidailyStore.getState().gameState?.phase).toBe(phase);
      });
    });
  });
}
